import {
  Button,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";

const COMPACT_ID_HEAD = 8;
const COMPACT_ID_TAIL = 6;
const COMPACT_ID_MAX = 16;

interface CopyableIdProps {
  value: string;
  label?: string;
  copiedMessage: string;
  copyLabel: string;
  className?: string;
  truncate?: boolean;
  displayValue?: string;
  /**
   * Quiet: the truncated id *is* the copy control — for dense metadata lines
   * where a 28px icon would blow the row height.
   */
  density?: "default" | "quiet";
}

export function compactIdDisplay(value: string): string {
  if (value.length <= COMPACT_ID_MAX) return value;
  return `${value.slice(0, COMPACT_ID_HEAD)}…${value.slice(-COMPACT_ID_TAIL)}`;
}

export function CopyableId({
  value,
  label,
  copiedMessage,
  copyLabel,
  className,
  truncate = true,
  displayValue,
  density = "default",
}: CopyableIdProps) {
  const { t } = useTranslation();
  const { copied, copy } = useCopyToClipboard({
    onCopy: density === "quiet" ? () => toast.success(copiedMessage) : undefined,
  });
  const display = displayValue ?? (truncate ? compactIdDisplay(value) : value);

  const handleCopy = (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
    void copy(value);
  };

  if (density === "quiet") {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={copied ? copiedMessage : copyLabel}
            className={cn(
              "inline-flex min-w-0 max-w-full cursor-pointer truncate rounded-sm font-mono text-caption tabular-nums",
              "text-muted-foreground transition-colors duration-150 hover:text-foreground",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
              className,
            )}
            onClick={handleCopy}
            onKeyDown={(event) => event.stopPropagation()}
          >
            {display}
          </button>
        </TooltipTrigger>
        <TooltipContent>{copied ? copiedMessage : value}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <span className={cn("inline-flex min-w-0 items-center gap-1.5", className)}>
      {label ? (
        <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      ) : null}
      <span
        className="text-data inline-flex min-w-0 items-baseline break-all font-mono normal-case text-foreground"
        aria-label={value}
        title={value}
      >
        {display}
      </span>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={copied ? copiedMessage : copyLabel}
            className="size-7 shrink-0 text-muted-foreground hover:text-foreground"
            onClick={handleCopy}
          >
            <Icon name={copied ? "check" : "copy"} size="xs" color="inherit" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{copied ? copiedMessage : copyLabel}</TooltipContent>
      </Tooltip>
      <span className="sr-only" aria-live="polite">
        {copied ? t("marketplace.orders.copyOrderIdSuccess") : ""}
      </span>
    </span>
  );
}
